import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { TableBody } from "@material-ui/core";
import { toast } from "react-toastify";
import ValidationLayout from "../../../AdminValidations";
import { AuthRoutes, SortOrder } from "../../../../enums";
import CollapsibleNe from "../../Collapsible";
import GroupOperation from "../../GroupOperations";
import AddPortService from "../../../../services/addPort.service";
import ManagePortsService from "../../../../services/managePorts.service";
import { TableConstants, HeadCellType, EditBandwidthFieldMapping } from "../../../../utils/appConstants";
import LoadingRow from "../../../DesignSystem/Table/Rows/LoadingRow";
import EmptyRow from "../../../DesignSystem/Table/Rows/EmptyRow";
import BandwidthTable from "../../BandwidthTable";
import SaleDrawer from "../../BandwidthTable/PutUpForSale";
import PdfViewer from "../../../PdfViewer";
import TableData from "../../TableData";
import SearchIcon from "../../../../assets/images/search-icon.svg";
import {
  SuccessMessage,
  ErrorCodeMessageMapping,
  ErrorCodesMapping,
  MY_SELLER_BUCKET_PRE_URL,
  TERMS_DEFAULT_FILE,
  ErrorMessages as Errors,
  ErrorMessages,
} from "../../../../utils/apiConstants";
import ErrorInfoIcon from "../../../../assets/images/error-info-icon.svg";
import SuccessInfoIcon from "../../../../assets/images/success-toast-checkmark-icon.svg";
import EditBandwidthDrawer from "../../EditBandwidth";
import ManageBandwidthService from "../../../../services/manageBandwidth.service";
import { sumCount, throwGenericToasterError } from "../../../../utils/methods";
import Button from "../../../DesignSystem/button";
import Input from "../../../DesignSystem/input";
import NodeView from "../../../../assets/images/node-view.svg";
import NodeViewSelected from "../../../../assets/images/node-view-selected.svg";
import ListView from "../../../../assets/images/list-icon.svg";
import ListViewSelected from "../../../../assets/images/list-icon-selected.svg";
import Dropdown from "../../../DesignSystem/dropdown_autocomplete";
import SwipeIcon from "../../../../assets/images/swipe-icon.svg";

const TypeOption = [
  {
    label: "Internet",
    value: "internet",
  },
  {
    label: "Cloud",
    value: "cloud",
  },
  {
    label: "Network Services",
    value: "network-services",
  },
];

export function usePutUpForSaleBandwidth(isMounted: boolean, refetch: () => void, loading: boolean, setLoading: (_: boolean) => void, lowerDrawer = false) {
  const [putForSaleOpen, setPutForSaleOpen] = useState<boolean>(false);
  const [selectedRowForSale, setSelectedRowForSale] = useState<any>();
  const [serviceDescription, setServiceDescription] = useState<string>("");
  const [onContractSelected, setOnContractSelected] = useState<boolean>(false);
  const [onDemandSelected, setOnDemandSelected] = useState<boolean>(false);
  const [onContractPrice, setOnContractPrice] = useState<string>("");
  const [onDemandPrice, setOnDemandPrice] = useState<string>("");
  const [type, setType] = useState<string>(TypeOption[0].value);
  const [saleEdit, setSaleEdit] = useState<boolean>(false);
  const [removeLoadingId, setRemoveLoadingId] = useState<string>("");
  const [saleTouched, setSaleTouched] = useState<boolean>(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [aggrementTypeInfo, setAggrementTypeInfo] = useState<{
    type: string;
    url: string;
  }>({
    type: "",
    url: "",
  });
  const [saleAggrementAccepted, setSaleAggrementAccepted] = useState<{
    on_contract: boolean;
    on_demand: boolean;
  }>({
    on_contract: false,
    on_demand: false,
  });

  const handleTermsOpenClick = useCallback(
    (type: string) => {
      setPdfViewerOpen(true);
      const fileName = type === "on_contract" ? selectedRowForSale.terms_on_contract_url : selectedRowForSale.terms_on_demand_url;
      setAggrementTypeInfo((prev) => {
        return {
          type,
          url: fileName ? MY_SELLER_BUCKET_PRE_URL + fileName : MY_SELLER_BUCKET_PRE_URL + TERMS_DEFAULT_FILE,
        };
      });
    },
    [setPdfViewerOpen, selectedRowForSale]
  );
  const [isDBPrice, setDBPrice] = useState<{
    onDemand: boolean;
    onContract: boolean;
  }>({
    onDemand: false,
    onContract: false,
  });
  const handleServiceDescription = useCallback(
    (e) => {
      setServiceDescription(e.target.value);
      setSaleTouched(true);
    },
    [setServiceDescription, setSaleTouched]
  );
  const handleOnContractPriceChange = useCallback(
    (e) => {
      if (e.target.value < 0) {
        return;
      }
      const split = e.target.value.split(".");
      if (split[1]) {
        if (split[1].length <= 2) {
          setOnContractPrice(e.target.value);
        }
      } else {
        setOnContractPrice(e.target.value);
      }
      setSaleTouched(true);
    },
    [setOnContractPrice, setSaleTouched]
  );

  const handleClosePutupForSale = useCallback(() => {
    setServiceDescription("");
    setPutForSaleOpen(false);
    setOnContractSelected(false);
    setOnDemandSelected(false);
    setSaleEdit(false);
    setOnDemandPrice("");
    setOnContractPrice("");
    setSaleTouched(false);
    setSaleAggrementAccepted({
      on_contract: false,
      on_demand: false,
    });
    setAggrementTypeInfo({
      type: "",
      url: "",
    });
    setDBPrice({
      onDemand: false,
      onContract: false,
    });
  }, [
    setSaleEdit,
    setSaleTouched,
    setServiceDescription,
    setPutForSaleOpen,
    setDBPrice,
    setOnContractSelected,
    setOnDemandSelected,
    setOnDemandPrice,
    setOnContractPrice,
    setSaleAggrementAccepted,
    setAggrementTypeInfo,
  ]);

  const handleSaleEdit = useCallback(
    (port: any) => {
      setSelectedRowForSale(port);
      setPutForSaleOpen(true);
      setServiceDescription(port.service_description);
      setOnContractSelected(!!port.on_contract_price);
      setOnDemandSelected(!!port.on_demand_price);
      setOnDemandPrice(port.on_demand_price);
      setOnContractPrice(port.on_contract_price);
      // setType(port.type);
      setSaleEdit(true);
      setSaleAggrementAccepted({
        on_contract: !!port.on_contract_price,
        on_demand: !!port.on_demand_price,
      });
      setDBPrice({
        onContract: !!port.on_contract_price,
        onDemand: !!port.on_demand_price,
      });
    },
    [
      type,
      setPutForSaleOpen,
      setSelectedRowForSale,
      setServiceDescription,
      setDBPrice,
      setSaleAggrementAccepted,
      setOnContractSelected,
      setOnDemandSelected,
      setOnDemandPrice,
      setOnContractPrice,
      setType,
      setSaleEdit,
    ]
  );
  const handleOnDemandPriceChange = useCallback(
    (e) => {
      if (e.target.value < 0) {
        return;
      }
      const split = e.target.value.split(".");
      if (split[1]) {
        if (split[1].length <= 2) {
          setOnDemandPrice(e.target.value);
        }
      } else {
        setOnDemandPrice(e.target.value);
      }
      setSaleTouched(true);
    },
    [setOnDemandPrice, setSaleTouched]
  );

  const handleOnContractSelected = useCallback(
    (e) => {
      let prevVal = null;
      setOnContractSelected((prev) => {
        prevVal = prev;
        return !prev;
      });
      setSaleTouched(true);
      if (prevVal) {
        setDBPrice((prev) => {
          return {
            ...prev,
            onContract: false,
          };
        });
        setSaleAggrementAccepted((prev) => {
          return {
            ...prev,
            on_contract: false,
          };
        });
      }
    },
    [setOnContractSelected, setSaleTouched, setDBPrice, setSaleAggrementAccepted]
  );
  const handleOnDemandSelected = useCallback(
    (e) => {
      let prevVal = null;
      setOnDemandSelected((prev) => {
        prevVal = prev;
        return !prev;
      });
      setSaleTouched(true);
      if (prevVal) {
        setDBPrice((prev) => {
          return {
            ...prev,
            onDemand: false,
          };
        });
        setSaleAggrementAccepted((prev) => {
          return {
            ...prev,
            on_demand: false,
          };
        });
      }
    },
    [setOnDemandSelected, setSaleTouched, setDBPrice, setSaleAggrementAccepted]
  );

  const handleTermsCloseClick = useCallback(
    (agrred?: boolean, agreementType?: string) => {
      // here
      setPdfViewerOpen(false);
      agrred &&
        agreementType &&
        setSaleAggrementAccepted((prev) => {
          return {
            ...prev,
            [agreementType]: agrred,
          };
        });
      if (agreementType) {
        if (agreementType === "on_contract") {
          handleOnContractSelected(1);
        }
        if (agreementType === "on_demand") {
          handleOnDemandSelected(1);
        }
      }
    },
    [setPdfViewerOpen, setSaleAggrementAccepted, handleOnContractSelected, handleOnDemandSelected]
  );
  const handlePutupForSaleClick = useCallback(() => {
    setLoading(true);
    if (saleEdit) {
      const payload: any = {
        serviceDescription,
        onContractPrice: onContractPrice && onContractSelected ? parseFloat(onContractPrice).toFixed(2) : 0,
        onDemandPrice: onDemandSelected && onDemandPrice ? parseFloat(onDemandPrice).toFixed(2) : 0,
      };

      ManageBandwidthService.editSaleDetails(payload, selectedRowForSale.bandwidth_detail_id)
        .then(() => {
          if (!isMounted) return;
          setLoading(false);
          handleClosePutupForSale();
          const message = SuccessMessage.putForSaleEditSuccessForBandwidth;
          toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          refetch();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setLoading(false);
          if (e.errorCode === ErrorCodesMapping[1010]) {
            toast.error(ErrorCodeMessageMapping[1010], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            refetch();
            handleClosePutupForSale();
          } else {
            throwGenericToasterError(e);
          }
        });
    } else {
      const payload: any = {
        bdId: selectedRowForSale?.bandwidth_detail_id,
        serviceDescription,
        onContractPrice: onContractPrice && onContractSelected ? parseFloat(onContractPrice) : 0,
        onDemandPrice: onDemandSelected && onDemandPrice ? parseFloat(onDemandPrice) : 0,
      };

      ManageBandwidthService.putUpBandwidthForSale(payload)
        .then(() => {
          if (!isMounted) return;
          setLoading(false);
          handleClosePutupForSale();
          const message = SuccessMessage.putBandwidthForSaleSuccess;
          toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          refetch();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          throwGenericToasterError(e);
          setLoading(false);
        });
    }
  }, [
    serviceDescription,
    onDemandSelected,
    onContractSelected,
    onContractPrice,
    onDemandPrice,
    selectedRowForSale?.bandwidth_detail_id,
    setLoading,
    handleClosePutupForSale,
    refetch,
  ]);
  const handlePutForSaleOpen = useCallback(
    (port: any) => {
      setServiceDescription(port.bandwidth_description);
      setSelectedRowForSale(port);
      setPutForSaleOpen(true);
    },
    [setPutForSaleOpen, setServiceDescription, setSelectedRowForSale, serviceDescription]
  );
  const handleRemoveClick = useCallback(
    (port: any) => {
      setRemoveLoadingId(port.bandwidth_detail_id);

      const payload = {
        bdId: port.bandwidth_detail_id,
      };
      ManageBandwidthService.removeFromMarketPlace(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.removeFromMarketPlaceSuccessForBandwidth, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          setRemoveLoadingId("");
          refetch();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setRemoveLoadingId("");
          if (e.errorCode === ErrorCodesMapping[1010]) {
            toast.error(ErrorCodeMessageMapping[1010], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            refetch();
          } else {
            throwGenericToasterError(e);
          }
        });
    },
    [setRemoveLoadingId, refetch]
  );
  const component = (
    <SaleDrawer
      saleTouched={saleTouched}
      aggrementAccepted={saleAggrementAccepted}
      onAggredTermsClick={handleTermsOpenClick}
      acceptAgreementBypass={handleTermsCloseClick}
      edit={saleEdit}
      type={type}
      isDBPrice={isDBPrice}
      loading={loading}
      selectedRowSale={selectedRowForSale}
      open={putForSaleOpen}
      onClose={handleClosePutupForSale}
      onContractSelected={onContractSelected}
      onDemandSelected={onDemandSelected}
      onContractPrice={onContractPrice}
      onDemandPrice={onDemandPrice}
      serviceDescription={serviceDescription}
      handleServiceDescription={handleServiceDescription}
      handleOnContractPriceChange={handleOnContractPriceChange}
      handleOnDemandPriceChange={handleOnDemandPriceChange}
      handlePutupForSaleClick={handlePutupForSaleClick}
      handleOnContractSelected={handleOnContractSelected}
      handleOnDemandSelected={handleOnDemandSelected}
      pdfViewerOpen={pdfViewerOpen}
      handleTermsCloseClick={handleTermsCloseClick}
      aggrementTypeInfo={aggrementTypeInfo}
      lowerDrawer={lowerDrawer}
    />
  );
  return {
    SaleDrawerComponent: component,
    handlePutForSaleOpen,
    handleSaleEdit,
    handleRemoveClick,
    removeLoadingId,
  };
}
